import * as THREE from "three";
import {
  SPECIES,
  type AnimState,
  type CollisionWorld,
  type LimbBox,
  type LimbId,
  type Stance,
  type TacticalKind,
  type Vec3,
  type WildlifeSpecies,
} from "@rietz/shared";
import { WILDLIFE_COATS } from "./palette.js";
import { SHADOWS_ENABLED } from "./shadows.js";
import type { CharacterGear, CharacterRig } from "./character.js";

/**
 * The low-poly quadrupeds — hare, roe deer, chamois.
 *
 * THE RIG IS THE HITBOX: every dimension is read off `SPECIES[species].boxes`,
 * the same two boxes the server raycasts against. The trunk fills the
 * `"thorax"` box; neck, skull and mask fill the `"head"` box. What stands
 * outside them is only what the server would not register a hit on anyway —
 * legs, ears, antlers, the rump patch, a centimetre of hoof below ground.
 *
 * It implements all of `CharacterRig` (twelve members as no-ops — an animal
 * has no name, shout, gear, magazine, lean, torch or ragdoll) so
 * `game/remotes.ts` holds one avatar type. Animation is procedural and driven
 * by DISTANCE TRAVELLED like the humanoid, in diagonal leg pairs. Zero
 * per-frame allocation.
 */

/** How fast the stance/death blends resolve, per second. Matches the
 *  humanoid's, so a carcass settles at the same rate a man's does. */
const BLEND_RATE = 9;

/** Swing amplitude, in radians at the hip, by animation state. */
const SWING_AMPLITUDE: Record<AnimState, number> = {
  idle: 0,
  walk: 0.5,
  sprint: 0.95,
  dead: 0,
};

/**
 * How far a dead animal settles into the ground once on its side — a carcass
 * resting exactly on the terrain sample floats visibly on any slope.
 */
const DEATH_SINK_M = 0.05;

/** Per-species dressing — everything the two hitboxes do not already say. */
interface Dressing {
  /** Ear length and thickness, metres. The hare's are the animal. */
  earLen: number;
  earW: number;
  /** What is on its head: nothing, a roebuck's short beams, or a hook. */
  headgear: "none" | "antler" | "hook";
  /** A pale rump patch on the rear face — the roe deer's departure signal. */
  rump: boolean;
  /** It goes flat instead of running, so a `"crouch"` stance folds it into a
   *  ball. Only the hare does; see `SpeciesDef.hidesAfterFlee`. */
  hides: boolean;
  /**
   * Vertical bound at full sprint, metres. The hare's is an order of magnitude
   * bigger: a hare hops.
   */
  hopM: number;
  /**
   * Radians of leg swing per metre travelled — roughly inverse to leg length.
   */
  swingPerM: number;
}

const DRESSING: Readonly<Record<WildlifeSpecies, Dressing>> = {
  hare: {
    earLen: 0.17,
    earW: 0.026,
    headgear: "none",
    rump: false,
    hides: true,
    hopM: 0.09,
    swingPerM: 7,
  },
  deer: {
    earLen: 0.1,
    earW: 0.042,
    headgear: "antler",
    rump: true,
    hides: false,
    hopM: 0.025,
    swingPerM: 2.2,
  },
  chamois: {
    earLen: 0.08,
    earW: 0.038,
    headgear: "hook",
    rump: false,
    hides: false,
    hopM: 0.018,
    swingPerM: 2.5,
  },
};

/**
 * Shared box geometries keyed by size, as in `character.ts`. Never disposed; a
 * species' sizes are fixed for the process.
 */
const geoCache = new Map<string, THREE.BoxGeometry>();

function boxGeo(w: number, h: number, d: number): THREE.BoxGeometry {
  const key = `${w}|${h}|${d}`;
  let g = geoCache.get(key);
  if (g === undefined) {
    g = new THREE.BoxGeometry(w, h, d);
    geoCache.set(key, g);
  }
  return g;
}

/**
 * The named hitbox for a species. Throws on a missing limb: both boxes are
 * frozen content, so this is a bug in `wildlife.ts`, not bad input.
 */
function limbBox(species: WildlifeSpecies, limb: "thorax" | "head"): LimbBox {
  const found = SPECIES[species].boxes.find((b) => b.limb === limb);
  if (found === undefined) {
    throw new Error(`wildlife species "${species}" has no "${limb}" box`);
  }
  return found;
}

/** Turns every mesh under `root` into a shadow caster/receiver. A no-op unless
 *  `?shadows=1`, exactly like the humanoid's — see `character.ts`. */
function markShadowCasters(root: THREE.Object3D): void {
  if (!SHADOWS_ENABLED) return;
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh !== true) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
}

class AnimalRig implements CharacterRig {
  readonly group = new THREE.Group();

  /** Bob and hide sink live here, so death does not fight them. */
  private readonly body = new THREE.Group();
  /**
   * The death roll's pivot at the trunk's centre of mass; `frame` shifts the
   * children back down so everything stays authored feet-at-origin. Rolling
   * about the FEET would swing a deer's body out of the hitbox it is still
   * being shot at through.
   */
  private readonly carcass = new THREE.Group();
  private readonly frame = new THREE.Group();
  /** Head group, so the ears and the horns move with it. */
  private readonly head = new THREE.Group();

  /** Four hips, in `LEG_ORDER`: front-left, front-right, rear-left, rear-right. */
  private readonly hips: THREE.Group[] = [];
  private readonly legs: THREE.Mesh[] = [];
  private readonly ears: THREE.Group[] = [];

  private readonly coatMat: THREE.MeshLambertMaterial;
  private readonly bellyMat: THREE.MeshLambertMaterial;
  private readonly hornMat: THREE.MeshLambertMaterial;
  private readonly faceMat: THREE.MeshLambertMaterial;

  private readonly dressing: Dressing;
  /** Leg length, metres: the gap between the trunk's underside and the ground. */
  private readonly legLen: number;
  /** Half a drawn leg's height, so the fold can re-centre the box it shrinks. */
  private readonly legHalfM: number;
  /** The trunk's centre height — where `carcass` pivots, and its rest position. */
  private readonly trunkCentreY: number;
  /** How far the mass centre falls when the animal goes onto its side. */
  private readonly deathDropM: number;
  /** How far the trunk sinks when a hare flattens itself. */
  private readonly hideSinkM: number;
  /** Resting ear pitch, so the hide fold can lay them back from a known start. */
  private readonly earRestX: number;

  /** Gait phase, radians, advanced by distance travelled. */
  private phase = 0;
  /** Idle breathing runs on a clock rather than on distance — nothing moves. */
  private clock = 0;
  private hide = 0;
  private death = 0;
  private lastX = 0;
  private lastZ = 0;
  private hasLast = false;

  constructor(species: WildlifeSpecies, id: string) {
    const coat = WILDLIFE_COATS[species];
    const dress = DRESSING[species];
    this.dressing = dress;

    this.group.name = `animal:${species}:${id}`;

    this.coatMat = new THREE.MeshLambertMaterial({
      color: coat.coat,
      flatShading: true,
    });
    this.bellyMat = new THREE.MeshLambertMaterial({
      color: coat.belly,
      flatShading: true,
    });
    this.hornMat = new THREE.MeshLambertMaterial({
      color: coat.horn,
      flatShading: true,
    });
    this.faceMat = new THREE.MeshLambertMaterial({
      color: coat.face,
      flatShading: true,
    });

    const T = limbBox(species, "thorax");
    const H = limbBox(species, "head");
    const tw = T.max.x - T.min.x;
    const th = T.max.y - T.min.y;
    const td = T.max.z - T.min.z;
    const tcy = (T.min.y + T.max.y) / 2;
    const tcz = (T.min.z + T.max.z) / 2;
    const hw = H.max.x - H.min.x;
    const hh = H.max.y - H.min.y;
    const hd = H.max.z - H.min.z;

    this.legLen = T.min.y;
    // Half the trunk's WIDTH is what is under it once it is on its flank, so
    // that is the height its centre loses; the extra centimetres are the settle.
    this.deathDropM = Math.max(0, tcy - tw * 0.5) + DEATH_SINK_M;
    // A hiding hare puts its belly on the ground: the whole leg length, less a
    // little, so the feet are still under it rather than through it.
    this.hideSinkM = this.legLen * 0.9;

    this.group.add(this.body);
    this.body.add(this.carcass);
    this.trunkCentreY = tcy;
    this.carcass.position.y = tcy;
    this.carcass.add(this.frame);
    this.frame.position.y = -tcy;

    // --- The trunk: the thorax hitbox, drawn ------------------------------
    const trunk = new THREE.Mesh(boxGeo(tw, th, td), this.coatMat);
    trunk.position.set(0, tcy, tcz);
    this.frame.add(trunk);

    // Counter-shaded underside: a pale belly is what separates an animal from a
    // crate at 60 m.
    const belly = new THREE.Mesh(
      boxGeo(tw * 0.84, th * 0.22, td * 0.82),
      this.bellyMat,
    );
    belly.position.set(0, T.min.y + th * 0.1, tcz);
    this.frame.add(belly);

    if (dress.rump) {
      // The roe deer's rump patch on the rear face — what you see of a deer
      // that has decided to leave.
      const rump = new THREE.Mesh(
        boxGeo(tw * 0.6, th * 0.5, 0.02),
        this.bellyMat,
      );
      rump.position.set(0, tcy + th * 0.14, T.max.z + 0.011);
      this.frame.add(rump);
    }

    // --- Neck and head: the head hitbox, split in two on screen (a single
    // block
    // is a plank), both together still filling exactly the box.
    const neck = new THREE.Mesh(
      boxGeo(hw * 0.7, hh * 0.85, hd * 0.5),
      this.coatMat,
    );
    neck.position.set(0, H.min.y + hh * 0.45, H.max.z - hd * 0.24);
    // Tipped forward (the top toward −Z): a rotation about +X carries +Y
    // toward +Z, so the neck leans out over the head end when it is negative.
    neck.rotation.x = -0.2;
    this.frame.add(neck);

    this.frame.add(this.head);
    this.head.position.set(0, H.max.y - hh * 0.33, H.min.z + hd * 0.31);

    const headW = hw;
    const headH = hh * 0.62;
    const headD = hd * 0.62;
    // The mask is part of the head's DEPTH: the skull gives up the two
    // centimetres it occupies.
    const maskT = 0.02;
    const skull = new THREE.Mesh(
      boxGeo(headW, headH, headD - maskT),
      this.coatMat,
    );
    skull.position.z = maskT * 0.5;
    this.head.add(skull);

    // The mask: a dark muzzle on hare and deer, the chamois's pale face — the
    // only
    // light thing on an animal that is otherwise a black shape on grey rock.
    const mask = new THREE.Mesh(
      boxGeo(headW * 0.82, headH * 0.55, maskT),
      this.faceMat,
    );
    mask.position.set(0, -headH * 0.08, -headD * 0.5 + maskT * 0.5);
    this.head.add(mask);

    // --- Ears --------------------------------------------------------------
    this.earRestX = 0.16;
    for (const side of [-1, 1]) {
      const pivot = new THREE.Group();
      pivot.position.set(side * headW * 0.3, headH * 0.42, headD * 0.12);
      pivot.rotation.x = this.earRestX;
      pivot.rotation.z = -side * 0.24;
      const ear = new THREE.Mesh(
        boxGeo(dress.earW, dress.earLen, dress.earW * 0.5),
        this.coatMat,
      );
      ear.position.y = dress.earLen * 0.5;
      pivot.add(ear);
      this.head.add(pivot);
      this.ears.push(pivot);
    }

    // --- Headgear ----------------------------------------------------------
    if (dress.headgear === "antler") {
      // A roebuck's beams are short and nearly vertical with one forward tine,
      // which is the shape that says "deer" in four boxes and no more.
      for (const side of [-1, 1]) {
        const beam = new THREE.Mesh(boxGeo(0.02, 0.15, 0.02), this.hornMat);
        beam.position.set(side * headW * 0.2, headH * 0.5 + 0.07, 0);
        beam.rotation.z = -side * 0.16;
        this.head.add(beam);

        const tine = new THREE.Mesh(boxGeo(0.016, 0.07, 0.016), this.hornMat);
        tine.position.set(side * headW * 0.24, headH * 0.5 + 0.11, -0.03);
        tine.rotation.x = -0.7;
        this.head.add(tine);
      }
    } else if (dress.headgear === "hook") {
      // The Krickel: a straight rise and a tip hooked hard forward, which is
      // both what a chamois wears and what the trophy in the item roster is.
      for (const side of [-1, 1]) {
        const shaft = new THREE.Mesh(boxGeo(0.016, 0.11, 0.016), this.hornMat);
        shaft.position.set(side * headW * 0.22, headH * 0.5 + 0.055, 0.01);
        this.head.add(shaft);

        const hook = new THREE.Mesh(boxGeo(0.015, 0.055, 0.015), this.hornMat);
        hook.position.set(side * headW * 0.22, headH * 0.5 + 0.115, -0.015);
        hook.rotation.x = -1.15;
        this.head.add(hook);
      }
    }

    // --- Legs: front pair toward −Z, rear toward +Z. The hip sits inside the
    // trunk so a swing never opens a gap; the leg runs 1 cm past the ground
    // plane.
    const legW = Math.max(0.02, tw * 0.17);
    const legH = this.legLen + 0.03;
    this.legHalfM = legH * 0.5;
    const hipZ = [T.min.z + td * 0.18, T.max.z - td * 0.18];
    for (const z of hipZ) {
      for (const side of [-1, 1]) {
        const pivot = new THREE.Group();
        pivot.position.set(side * (tw * 0.5 - legW * 0.6), T.min.y + 0.02, z);
        const leg = new THREE.Mesh(boxGeo(legW, legH, legW), this.coatMat);
        leg.position.y = -legH * 0.5;
        pivot.add(leg);
        this.frame.add(pivot);
        this.hips.push(pivot);
        this.legs.push(leg);
      }
    }

    markShadowCasters(this.group);
  }

  setPose(
    pos: Vec3,
    yaw: number,
    _pitch: number,
    stance: Stance,
    anim: AnimState,
    dtMs: number,
  ): void {
    const dt = Math.min(Math.max(dtMs, 0), 100) / 1000;
    this.clock += dt;

    // --- Root: `pos` is the FEET, yaw 0 faces −Z — the humanoid's conventions.
    this.group.position.set(pos.x, pos.y, pos.z);
    this.group.rotation.y = yaw;

    // --- Gait phase from distance travelled ---------------------------------
    let moved = 0;
    if (this.hasLast) {
      const dx = pos.x - this.lastX;
      const dz = pos.z - this.lastZ;
      moved = Math.sqrt(dx * dx + dz * dz);
      // A herd respawn or a big correction must not spin the legs.
      if (moved > 2) moved = 0;
    }
    this.lastX = pos.x;
    this.lastZ = pos.z;
    this.hasLast = true;

    this.phase += moved * this.dressing.swingPerM;
    if (this.phase > Math.PI * 2) this.phase -= Math.PI * 2;

    // --- Blends -------------------------------------------------------------
    const k = Math.min(1, dt * BLEND_RATE);
    this.death += ((anim === "dead" ? 1 : 0) - this.death) * k;
    // A DEAD ANIMAL DOES NOT HIDE — folded into the target so a flat hare
    // unwinds through one blend.
    const wantHide =
      this.dressing.hides && stance === "crouch" && anim !== "dead" ? 1 : 0;
    this.hide += (wantHide - this.hide) * k;

    const alive = 1 - this.death;
    const amp = SWING_AMPLITUDE[anim] * alive * (1 - this.hide);

    // --- Bob and sink -------------------------------------------------------
    // Idle is a slow breath; moving is a bound, and a hare's is the read.
    const bob =
      amp === 0
        ? Math.sin(this.clock * 1.6) * 0.004 * alive
        : Math.abs(Math.sin(this.phase)) * this.dressing.hopM * (amp / 0.95);
    this.body.position.y = bob - this.hideSinkM * this.hide;

    // --- Death: onto its flank about the trunk's centre. See `carcass`.
    this.carcass.rotation.z = this.death * (Math.PI / 2);
    this.carcass.position.y = this.trunkCentreY - this.deathDropM * this.death;

    // --- Legs: diagonal pairs. Written every frame, dead or alive — `amp` is
    // already 0 for a carcass.
    const swing = Math.sin(this.phase) * amp;
    const counter = Math.sin(this.phase + Math.PI) * amp;
    const fold = 1 - 0.8 * this.hide;
    for (let i = 0; i < this.hips.length; i++) {
      const pivot = this.hips[i];
      const leg = this.legs[i];
      if (pivot === undefined || leg === undefined) continue;
      // 0 and 3 are front-left and rear-right — the diagonal that moves first.
      pivot.rotation.x = i === 0 || i === 3 ? swing : counter;
      leg.scale.y = fold;
      // Re-centred as it shrinks, so a folded leg tucks up under the belly
      // rather than shortening from both ends and hanging in the air.
      leg.position.y = -this.legHalfM * fold;
    }

    // --- Ears: laid flat when the hare goes to ground — ears up is the one
    // part visible in stubble.
    const earX = this.earRestX + 1.25 * this.hide;
    for (const ear of this.ears) ear.rotation.x = earX;
  }

  // --- Everything a person has and an animal does not: twelve no-ops by
  // DESIGN, so `game/remotes.ts` holds one avatar type. `setTint`/
  // `setGearTones` must stay inert — a deer that took a player tint would be a
  // bright red deer.

  setTint(_hex: number): void {}
  setGearTones(_gear: number, _accent: number): void {}
  setGear(_gear: CharacterGear | null): void {}
  setReloading(_reloading: boolean): void {}
  setLean(_lean: -1 | 0 | 1): void {}
  setTactical(_tac: TacticalKind | null): void {}
  updateTactical(_world: CollisionWorld | null): void {}
  setName(_name: string, _team?: boolean, _accent?: string): void {}
  setBark(_text: string | null): void {}
  // A deer has no flinch and no ragdoll: `carcass` above is its whole death.
  hit(_limb: LimbId, _from: Vec3 | null): void {}
  kill(_from: Vec3 | null, _limb: LimbId | null): void {}
  updateNameTag(_cameraPos: THREE.Vector3): void {}

  dispose(): void {
    // Geometries are shared and long-lived (see `geoCache`); the four materials
    // are this rig's own and nothing else's.
    this.coatMat.dispose();
    this.bellyMat.dispose();
    this.hornMat.dispose();
    this.faceMat.dispose();
    this.group.clear();
  }
}

/**
 * One animal, built from its own hitboxes. `id` only names the node. No per-id
 * size or colour variation — three species that each look like themselves is
 * the whole read.
 */
export function createAnimal(
  species: WildlifeSpecies,
  id: string,
): CharacterRig {
  return new AnimalRig(species, id);
}
